import {
  Alert,
  Box,
  Button,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/contexts/AuthContext';
import { apiHelper, auth } from '@/helpers/ApiHelper';

export function LoginPage(): ReactElement {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [devEmail, setDevEmail] = useState('mohit@detectinspections.com');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const apiBase = import.meta.env.VITE_API_URL ?? '';

  const onGoogleSignIn = (): void => {
    window.location.assign(`${apiBase}/api/auth/google/start`);
  };

  const onDevLogin = async (): Promise<void> => {
    setError(null);
    setSubmitting(true);
    try {
      const { token } = await auth.devLogin(devEmail);
      apiHelper.setToken(token);
      await refresh();
      navigate('/', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Dev login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.deeper',
        p: 3,
      }}
    >
      <Stack
        spacing={3}
        alignItems="center"
        sx={{ width: '100%', maxWidth: { xs: '100%', sm: 360, md: 480 } }}
      >
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontWeight: 700,
            fontSize: { xs: 22, md: 28 },
            color: 'primary.main',
            textShadow: '0 0 8px rgba(217, 119, 87, 0.55)',
            letterSpacing: '-0.3px',
            display: 'flex',
            alignItems: 'baseline',
            gap: 1,
          }}
        >
          <Box component="span" sx={{ fontSize: { xs: 24, md: 32 } }}>
            🕵🏽
          </Box>
          sherlock
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              width: 10,
              height: 18,
              backgroundColor: 'primary.main',
              boxShadow: '0 0 5px rgba(217, 119, 87, 0.55)',
              animation: 'blink 1.1s steps(1) infinite',
            }}
            aria-hidden="true"
          />
        </Typography>

        <Typography variant="caption" sx={{ color: 'text.faintest' }}>
          internal AI-triage dashboard for detect
        </Typography>

        <Button variant="contained" size="large" onClick={onGoogleSignIn} fullWidth>
          ▸ sign in with google
        </Button>

        <Divider sx={{ width: '100%', color: 'text.faintest', fontSize: 11 }}>
          or (dev only)
        </Divider>

        <Stack spacing={2} sx={{ width: '100%' }}>
          <TextField
            label="Email (@detectinspections.com)"
            value={devEmail}
            onChange={(e) => setDevEmail(e.target.value)}
            size="small"
            fullWidth
          />
          <Button
            variant="outlined"
            onClick={onDevLogin}
            disabled={submitting || !devEmail}
            fullWidth
          >
            Dev login (bypass Google)
          </Button>
          <Typography variant="caption" sx={{ color: 'text.faintest' }}>
            Dev login only works when SHERLOCK_DEV_LOGIN_ENABLED=true. Disabled in prod.
          </Typography>
        </Stack>

        <Typography variant="caption" sx={{ color: 'text.faintest' }}>
          @detectinspections.com only
        </Typography>

        {error && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        )}
      </Stack>
    </Box>
  );
}
